/* eslint-disable @webfleet/custom-rules/no-deep-imports */
import React from 'react'
import cssVars from 'css-vars-ponyfill'
import { setAutoFreeze } from 'immer'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import type { Store } from 'redux'
import createLogger from 'redux-logger'
import { Router } from 'wouter'

import { Globals } from '@wf-sl/common-utilities'
import { defineMomentLocales } from '@wf/datetime'
import { configureStore, createConfigurePushThunk, createTokenRefreshScheduler, logger, moduxReducers } from '@wf/modux'

import '@fontsource/noto-sans/400.css'
import '@fontsource/noto-sans/500.css'
import '@fontsource/noto-sans/700.css'

import { Logo } from './common/components/logo'
import { hasCssCustomProperties } from './common/utils/css-vars'
import './globals-setup'
import { BaseModule } from './loadable-modules'
import { moduleConfig } from './module-config'

import './main.css'

const isDev = Globals.get('NODE_ENV', 'development') !== 'production'

// Log version info
logger.log(`${Globals.get('DISPLAY_TITLE', 'DEVELOPER PORTAL')} Version ${Globals.get('VERSION', '0.0.0')}`)

// Start the token refresh scheduler. It will trigger an explicit token refresh every x minutes.
// The refresh interval can be configured in the globals-setup.js. The default value is 30 minutes.
const TOKEN_REFRESH_INTERVAL_MINUTES = Globals.get('TOKEN_REFRESH_INTERVAL_MINUTES', 30)
const tokenRefreshScheduler = createTokenRefreshScheduler(TOKEN_REFRESH_INTERVAL_MINUTES * 60 * 1000)
logger.log('Starting token refresh scheduler')
tokenRefreshScheduler.start()

// https://immerjs.github.io/immer/freezing/
setAutoFreeze(isDev)

// Provide client-side support for CSS custom properties in legacy browsers
// See also https://github.com/jhildenbiddle/css-vars-ponyfill
if (!hasCssCustomProperties()) {
    cssVars({
        onError: function (message: string, node: any, xhr: any, url: string) {
            if (isDev) {
                console.warn('cssVars error', message, node, xhr, url)
            }
        },
        onlyLegacy: true,
        watch: true
    })
}

// Setup root component
function createRootContainer(store: Store) {
    return function Root(): React.JSX.Element {
        const options = {
            appName: Globals.get('DISPLAY_TITLE', 'DEVELOPER PORTAL'), // appName can be set directly w/o Globals, e.g. if translation is needed
            appVersion: Globals.get('DISPLAY_VERSION', '0.0.0'),
            enableApplicationSwitcher: false,
            enableHeaderModuleTitle: true,
            enableNavigation: false,
            enableNavigationMenu: false,
            routePathFallbackNoMatch: `~${Globals.get('BASE_ROUTE_PATH', '')}/oops`,
            routePathFallbackRestricted: `~${Globals.get('BASE_ROUTE_PATH', '')}/access-denied`
        }

        return (
            <Provider store={store}>
                <Router base={Globals.get('BASE_ROUTE_PATH', '')}>
                    <BaseModule {...{ moduleConfig, options }} logo={<Logo />} />
                </Router>
            </Provider>
        )
    }
}

// Setup supported locales for localized formatting with Modux
// ATTENTION: If any locale is removed/added please also update the copy:locale script of the package.json
const supportedLocales = ['de-DE', 'en-GB', 'en-US', 'es-ES', 'nl-NL', 'pl-PL']

// Enable Redux debug logging in browser console depending on Globals (either by build config or URL query parameter)
const enableReduxLogger = Globals.get('REDUX_LOGGER', false)

const reduxMiddleWares = []
if (enableReduxLogger) {
    reduxMiddleWares.push(createLogger())
}

// Setup Redux store
const store = configureStore(moduxReducers, reduxMiddleWares, {}, []) as any

const { dispatch } = store

// If you need settings in your app, you have to provide the dedicated settings bundle as filter for the fetch below
// const settingsFetchFilter = new effectiveSettings.FilterBuilder().withBundle('my-settings-bundle')

// Bootstrap and mount root component
Promise.all([
    // Define moment locales corresponding to supported locales
    defineMomentLocales(supportedLocales)
    // Enable dispatching of following action to retrieve settings for the above configured settings bundle
    // dispatch(effectiveSettings.actions.fetch(settingsFetchFilter.build()))
]).then(() => {
    const pushThrottlingInterval = Globals.get('DEFAULT_PUSH_THROTTLING_INTERVAL', 10000)
    dispatch(createConfigurePushThunk(pushThrottlingInterval, {}))
    const Root = createRootContainer(store)
    const container = document.getElementById('app')
    if (container) {
        createRoot(container).render(<Root />)
    }
})
